/**
 * Canonical currency formatting utilities.
 *
 * ALL money values in this application are stored as integer cents.
 * These functions accept cents and produce locale-formatted dollar strings.
 *
 * Registered in COMPONENT_MAP as the single source of truth for currency display.
 * Any second implementation of currency formatting is a defect, not a shortcut.
 *
 * @module utils/formatCurrency
 */

/**
 * Format cents as a full currency string with thousands separators and 2 decimal places.
 * Example: 123456 → "$1,234.56"
 *
 * @param {number|string|null|undefined} cents - Amount in cents (integer minor units)
 * @returns {string} Formatted currency string (e.g., "$1,234.56")
 */
export function formatCurrency(cents) {
  const num = (Number(cents) || 0) / 100;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
}

/**
 * Format cents as a compact currency string for KPIs and dashboard cards.
 * Example: 1234500 → "$12.3K", 123456789 → "$1.2M"
 *
 * @param {number|string|null|undefined} cents - Amount in cents
 * @returns {string} Abbreviated currency string (e.g., "$12.3K")
 */
export function formatCurrencyCompact(cents) {
  const num = (Number(cents) || 0) / 100;
  if (num >= 1000000) return '$' + (num / 1000000).toFixed(1) + 'M';
  if (num >= 1000) return '$' + (num / 1000).toFixed(1) + 'K';
  return '$' + num.toFixed(0);
}

/**
 * Format cents as whole dollars (no decimal places) with thousands separators.
 * Example: 123456 → "$1,235"
 *
 * @param {number|string|null|undefined} cents - Amount in cents
 * @returns {string} Rounded currency string (e.g., "$1,235")
 */
export function formatCurrencyNoCents(cents) {
  const num = (Number(cents) || 0) / 100;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
}

/**
 * Format a raw dollar amount (NOT cents) as currency.
 * Use ONLY at form-display boundaries where the value is already in dollars.
 * Prefer formatCurrency(cents) everywhere else.
 *
 * @param {number|null|undefined} dollars - Amount in dollars
 * @returns {string} Formatted currency string (e.g., "$1,234.56")
 */
export function formatDollars(dollars) {
  const num = Number(dollars) || 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
}
